import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  TextField, 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Stack, 
  Divider, 
  useTheme, 
  useMediaQuery,
  Chip,
  InputAdornment,
  TablePagination,
  Skeleton
} from "@mui/material";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import itLocale from "date-fns/locale/it";
import { cicassService } from "../../../_services";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';

const ShiftChangeHistoryPage = (props) => {
  const apiEndpointShiftHistory = "shift/allrequest/1";
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [shiftHistory, setShiftHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const minDate = new Date(new Date().getFullYear() - 1, 0, 1);
  const maxDate = new Date(new Date().getFullYear() + 1, 11, 31);

  useEffect(() => {
    refreshGrid();
  }, [selectedDate]);

  async function refreshGrid() {
    setLoading(true);
    setPage(0); // Reset pagina al cambio mese
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const response = await cicassService.getAll(apiEndpointShiftHistory + `/${year}/${month}`);
      setShiftHistory(response.data || []);
    } catch (error) {
      console.error("Errore caricamento storico:", error);
    } finally {
      setLoading(false);
    }
  }

  const handlePrevMonth = () => {
    const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
    if (newDate >= minDate) {
      setSelectedDate(newDate);
    }
  };

  const handleNextMonth = () => {
    const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
    if (newDate <= maxDate) {
      setSelectedDate(newDate);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Logica di filtraggio
  const filteredHistory = shiftHistory.filter(item => 
    item.fromDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.toDescription?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Dati paginati
  const paginatedHistory = filteredHistory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderSkeleton = () => (
    Array.from(new Array(rowsPerPage)).map((_, index) => (
      <TableRow key={index}>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="circular" width={20} height={20} /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
      </TableRow>
    ))
  );

  const renderMobileSkeleton = () => (
    Array.from(new Array(3)).map((_, index) => (
      <Paper key={index} sx={{ p: 2, borderRadius: 2 }}>
        <Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" />
      </Paper>
    ))
  );

  return (
    <>
      <div style={{ margin: "1.2rem 0 .5rem 1.2rem" }}>
        <Typography variant="h6" component="h2">
          {t("ShiftChangeHistory")}
        </Typography>
      </div>

      <Box sx={{ margin: "0 1.2rem 0.5rem 1.2rem" }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', lg: 'flex-end' } }}>
            <TextField
              size="small"
              placeholder={t("Cerca") + "..."}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 250 }, bgcolor: 'background.paper' }}
            />

            <Tooltip title={t("Mese Precedente")}>
              <IconButton 
                onClick={handlePrevMonth} 
                disabled={selectedDate.getFullYear() <= minDate.getFullYear() && selectedDate.getMonth() <= minDate.getMonth()}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <LocalizationProvider dateAdapter={AdapterDateFns} locale={itLocale}>
              <DatePicker
                views={["year", "month"]}
                minDate={minDate}
                maxDate={maxDate}
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} helperText={null} />}
              />
            </LocalizationProvider>

            <Tooltip title={t("Mese Successivo")}>
              <IconButton 
                onClick={handleNextMonth} 
                disabled={selectedDate.getFullYear() >= maxDate.getFullYear() && selectedDate.getMonth() >= maxDate.getMonth()}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {!loading && filteredHistory.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
            <Typography color="text.secondary">{t("Nessun dato trovato")}</Typography>
          </Paper>
        ) : isMobile ? (
          <Box>
            <Stack spacing={2}>
              {loading ? renderMobileSkeleton() : paginatedHistory.map((row, index) => (
                <Paper key={index} elevation={2} sx={{ p: 2, borderRadius: 2, borderLeft: '5px solid', borderColor: row.confirmedAt ? 'success.main' : row.refusedAt ? 'error.main' : 'primary.main' }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                        {t("Dettaglio Turno")}
                      </Typography>
                      {row.confirmedAt && <CheckCircleIcon color="success" fontSize="small" />}
                      {row.refusedAt && <CancelIcon color="error" fontSize="small" />}
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.fromDescription}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5, color: 'text.disabled' }}>
                        <ArrowForwardIcon fontSize="inherit" sx={{ mr: 1 }} />
                        <Typography variant="caption">{t("Cambiato con")}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.toDescription}</Typography>
                    </Box>
                    <Divider />
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {row.confirmedAt && <Chip size="small" label={`${t("confermato")}: ${formatDate(row.confirmedAt)}`} color="success" variant="outlined" />}
                      {row.refusedAt && <Chip size="small" label={`${t("rifiutato")}: ${formatDate(row.refusedAt)}`} color="error" variant="outlined" />}
                      {row.deletedAt && <Chip size="small" label={`${t("eliminato")}: ${formatDate(row.deletedAt)}`} variant="outlined" />}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : (
          <Box component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>{t("fromDescription")}</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', width: 50 }} />
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>{t("toDescription")}</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>{t("confirmedAt")}</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>{t("refusedAt")}</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>{t("deletedAt")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? renderSkeleton() : paginatedHistory.map((row, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
                      <TableCell>{row.fromDescription}</TableCell>
                      <TableCell><ArrowForwardIcon color="disabled" fontSize="small" /></TableCell>
                      <TableCell>{row.toDescription}</TableCell>
                      <TableCell align="center">{formatDate(row.confirmedAt)}</TableCell>
                      <TableCell align="center">{formatDate(row.refusedAt)}</TableCell>
                      <TableCell align="center">{formatDate(row.deletedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredHistory.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("Righe per pagina")}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("di")} ${count !== -1 ? count : `${t("più di")} ${to}`}`}
          sx={{ mt: 1, borderTop: isMobile ? '1px solid' : 'none', borderColor: 'divider' }}
        />
      </Box>
    </>
  );
};

export default ShiftChangeHistoryPage;